import { Avatar, DogsInPill, Icon } from '../common';
import { PanelEmpty } from './PanelEmpty';
import { WeekHeader } from './WeekHeader';
import { MIN_HELPERS_PER_SHIFT, onDutyTeams } from './today';
import { dayName } from '../staff/week';
import { SLOT_HOURS } from '@bbbk/schema';
import type { Holiday, Shift, Staff } from '../staff/types';

// The week, Friday→Thursday — a READ-ONLY mirror of the rota grid
// (daily-command-strip AC20). No ADD SHIFT: the rota owns editing, and a second
// place to change a shift is a second place for two people to change it at once.
//
// No money line either. Hours and wages already live at the foot of each rota
// week, and a second place to read the same number is a second place for it to
// disagree.

export interface WeekPanelProps {
  days: string[];
  offset: number;
  onStep: (delta: number) => void;
  shifts: Shift[];
  staff: Staff[];
  // Dogs in on each day, from the shared occupancyByDate rule — so this column
  // agrees with the rail's headcount and the roster's own load by construction.
  dogsByDate: Map<string, number>;
  // Who is on leave each day — the rota's own derivation, so the mirror and the
  // roster cannot disagree about who is off.
  leaveFor?: Map<string, Holiday[]>;
  today: string;
  onOpenRota: () => void;
  loading?: boolean;
  // A window's request did not land. The grid still draws what is on hand, but
  // says so — an undercounted dog load or a missing shift is indistinguishable
  // from a quiet week unless the panel names which it is (PR #164, codex P2).
  failed?: boolean;
  // Opens the helper editor. It lives HERE rather than on the rail: a rail
  // helper cannot be a button, because the segment it sits in is one.
  onPickHelper?: (s: Staff) => void;
}

export function WeekPanel({
  days,
  offset,
  onStep,
  shifts,
  staff,
  dogsByDate,
  leaveFor,
  today,
  onOpenRota,
  loading,
  failed,
  onPickHelper,
}: WeekPanelProps) {
  const teams = days.map((d) => ({ date: d, ...onDutyTeams(shifts, staff, d) }));
  const byId = new Map(staff.map((p) => [p.id, p]));
  const leave = days.map((d) => ({
    date: d,
    names: (leaveFor?.get(d) ?? [])
      .map((h) => byId.get(h.staffId)?.name.split(' ')[0])
      .filter(Boolean)
      .join(', '),
  }));
  const filled = teams.reduce((n, t) => n + t.am.length + t.pm.length, 0);
  const helpers = new Set(teams.flatMap((t) => t.onDuty.map((s) => s.id))).size;

  // NOTHING AT ALL, not merely nobody rostered. A booked week with no coverage
  // is the week most worth looking at, and this used to replace it with a
  // sentence — hiding the per-day dog counts and every short-staffed warning
  // exactly when they mattered (PR #164, codex P2). The empty presentation is
  // now reserved for a week with neither shifts nor dogs, where the grid really
  // would be seven blank columns.
  //
  // AND NOT WHEN THE REQUEST FAILED. A failed first load leaves the held shifts
  // and bookings empty, which is indistinguishable from a genuinely quiet week
  // — so this branch ran, returned before the failure banner below, and
  // reported "Nobody is rostered this week" with complete confidence about a
  // week it had never read (PR #164, codex P1). Emptiness is only a fact once
  // something has come back to say so.
  const dogs = days.reduce((n, d) => n + (dogsByDate.get(d) ?? 0), 0);
  if (!loading && !failed && filled === 0 && dogs === 0) {
    return (
      <div className="cs-panel">
        <div className="cs-panel-head">
          <Icon name="users" size={13} className="cs-panel-ico" />
          <WeekHeader days={days} onStep={onStep} />
          <button type="button" className="cs-panel-link" onClick={onOpenRota}>
            Open the rota →
          </button>
        </div>
        <PanelEmpty
          icon="calendar"
          line="Nobody is rostered this week"
          detail="Shifts you add on the Rota page appear here — AM and PM for each of the seven days, with that day’s headcount beside them."
          action={{ label: 'Open the rota', onClick: onOpenRota }}
        />
      </div>
    );
  }

  // A NAME, sized to be read across a room rather than squinted at.
  const name = (s: Staff) => {
    const inner = (
      <>
        <Avatar name={s.name} id={s.id} size={22} />
        <span className="n">{s.name.split(' ')[0]}</span>
      </>
    );
    return onPickHelper ? (
      <button
        type="button"
        className="cs-wk-name clickable"
        key={s.id}
        onClick={() => onPickHelper(s)}
        title={`Edit ${s.name}`}
      >
        {inner}
      </button>
    ) : (
      <div className="cs-wk-name" key={s.id}>
        {inner}
      </div>
    );
  };

  // ONE BAND PER SHIFT, running the width of the week.
  //
  // AM and PM used to be stacked inside each day column, which meant answering
  // "who is on in the mornings this week?" — the question this panel exists for
  // — by scanning down and across seven separate times. As two bands the eye
  // reads straight along one row, and the two shifts can be compared without
  // holding either in your head.
  const band = (label: string, period: 'am' | 'pm') => (
    <div className={'cs-wk-band ' + period} role="row">
      <div className={'cs-wk-band-label cs-shift ' + period} role="rowheader">
        {label}
      </div>
      {teams.map((t) => {
        const team = period === 'am' ? t.am : t.pm;
        return (
          <div
            className={'cs-wk-cell' + (t.date === today ? ' today' : '')}
            key={t.date}
            role="cell"
          >
            {team.length ? team.map(name) : <div className="cs-none">—</div>}
            {team.length < MIN_HELPERS_PER_SHIFT && (
              <div className="cs-wk-short">
                <Icon name="alert-triangle" size={9} /> short
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="cs-panel">
      <div className="cs-panel-head">
        <Icon name="users" size={13} className="cs-panel-ico" />
        <span className="cs-panel-label">On duty</span>
        <WeekHeader days={days} onStep={onStep} />
        <button type="button" className="cs-panel-link" onClick={onOpenRota}>
          Open the rota →
        </button>
      </div>

      {failed && (
        <div className="cs-wk-failed" role="status">
          <Icon name="alert-triangle" size={12} />
          This week could not be loaded in full — what is shown may be incomplete.
        </div>
      )}

      <div className={'cs-week' + (loading ? ' is-loading' : '')} role="table" aria-label="The week's roster">
        <div className="cs-wk-days" role="row">
          <div className="cs-wk-band-label" role="columnheader" />
          {teams.map((t) => (
            <div
              className={'cs-wk-day' + (t.date === today ? ' today' : '')}
              key={t.date}
              role="columnheader"
            >
              <span className="cs-wk-dow">{dayName(t.date)}</span>
              <span className="cs-wk-dom">{Number(t.date.slice(8, 10))}</span>
              <DogsInPill count={dogsByDate.get(t.date) ?? 0} showZero={false} />
            </div>
          ))}
        </div>
        {leave.some((l) => l.names) && (
          <div className="cs-wk-leave-row" role="row">
            <div className="cs-wk-band-label" role="rowheader">
              <span className="sr-only">On leave</span>
            </div>
            {leave.map((l) => (
              <div className="cs-wk-cell" key={l.date} role="cell">
                {l.names && (
                  <span className="cs-wk-leave" title={`${l.names} on leave`}>
                    {l.names} on leave
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {band('AM', 'am')}
        {band('PM', 'pm')}
      </div>

      <div className="cs-panel-foot">
        <span>
          {helpers} helper{helpers === 1 ? '' : 's'} · {filled} shift{filled === 1 ? '' : 's'} ·{' '}
          {(filled * SLOT_HOURS).toFixed(1)}h
        </span>
      </div>
    </div>
  );
}
